import React from "react";
import { Dialog, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import { fontFamily } from "../../Constants/constants";
import "./ParallelProcessModal.css";

interface ClassifiedDoc {
  doc_type: string;
  confidence: number;
  pages: number[];
}

interface UploadProgress {
  fileName: string;
  status: "pending" | "processing" | "completed" | "failed";
  message: string;
  confidenceLevel?: number | null;
  documentType?: string | null;
  uploadType?: number | null;
  totalSplitted?: number | null;
  classifiedDocs?: ClassifiedDoc[];
}

interface ParallelProcessModalProps {
  open: boolean;
  uploadProgress: Record<string, UploadProgress>;
  title?: string;
  uploadingMessage?: string;
  processingMessage?: string;
  onCancel?: () => void;
  onClose?: () => void;
}

export const ParallelProcessModal: React.FC<ParallelProcessModalProps> = ({
  open,
  uploadProgress,
  title = "File Upload in Progress",
  uploadingMessage = "files simultaneously...",
  processingMessage = "Files are being processed in parallel for faster upload",
  onCancel,
  onClose,
}) => {
  const entries = Object.entries(uploadProgress);
  const uploadCount = entries.length;
  const isSingleUpload = uploadCount === 1;

  const allDone =
    uploadCount > 0 && entries.every(([, p]) => p.status === "completed" || p.status === "failed");

  // Summary stats
  const completedEntries = Object.values(uploadProgress).filter((p) => p.status === "completed");
  const classifiedCount = completedEntries.filter((p) => p.uploadType != null).length;
  const unidentifiedCount = completedEntries.filter((p) => p.uploadType == null).length;
  const confidenceValues = completedEntries
    .map((p) => p.confidenceLevel)
    .filter((c): c is number => c != null);
  const avgConfidence =
    confidenceValues.length > 0
      ? Math.round(confidenceValues.reduce((a, b) => a + b, 0) / confidenceValues.length)
      : null;
  const totalPages = completedEntries.reduce((sum, p) => {
    const docs: ClassifiedDoc[] = (p as any).classifiedDocs || [];
    return sum + docs.reduce((s, d) => s + (d.pages?.length ?? 0), 0);
  }, 0);

  const showSummary = completedEntries.length > 0;

  const handleCancel = () => {
    const userConfirmed = window.confirm(
      isSingleUpload
        ? "Are you sure you want to cancel uploading this file?"
        : "Are you sure you want to cancel uploading these files?"
    );
    if (userConfirmed) {
      if (onCancel) onCancel();
      else window.location.reload();
    }
  };

  const handleDone = () => {
    if (onClose) onClose();
    else if (onCancel) onCancel();
    else window.location.reload();
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { borderRadius: 10, padding: 20 } }}
    >
      <div className="parallel-modal-container">
        <div className="parallel-modal-header">
          <h6 className="parallel-modal-title" style={{ fontFamily }}>
            {title}
          </h6>
          {!allDone && (
            <button onClick={handleCancel} className="modal-close-btn" aria-label="Close">
              &#x2715;
            </button>
          )}
        </div>

        {showSummary && (
          <div className="parallel-modal-summary" style={{ fontFamily }}>
            <div className="parallel-modal-summary-item classified">
              <span className="parallel-modal-summary-count">{classifiedCount}</span>
              <span className="parallel-modal-summary-label">Classified</span>
            </div>
            <div className="parallel-modal-summary-item unidentified">
              <span className="parallel-modal-summary-count">{unidentifiedCount}</span>
              <span className="parallel-modal-summary-label">Not Identified</span>
            </div>
            {totalPages > 0 && (
              <div className="parallel-modal-summary-item pages">
                <span className="parallel-modal-summary-count">{totalPages}</span>
                <span className="parallel-modal-summary-label">Total Pages</span>
              </div>
            )}
            {avgConfidence != null && (
              <div className="parallel-modal-summary-item confidence">
                <span className="parallel-modal-summary-count">{avgConfidence}%</span>
                <span className="parallel-modal-summary-label">Avg Confidence</span>
              </div>
            )}
          </div>
        )}

        <div className="parallel-modal-progress-list">
          {entries.map(([index, progress]) => {
            const isDone = progress.status === "completed" || progress.status === "failed";
            const docLabel =
              progress.status === "completed"
                ? progress.uploadType != null
                  ? progress.documentType || "Classified"
                  : "Not Identified"
                : null;

            const overallConf =
              progress.status === "completed" && progress.confidenceLevel != null
                ? `${progress.confidenceLevel}%`
                : null;

            const docs: ClassifiedDoc[] = progress.classifiedDocs || [];

            return (
              <div key={index} className="parallel-modal-file-block">
                {/* ── File header row ── */}
                <div className="parallel-modal-progress-item">
                  <div className={`parallel-modal-status-indicator ${progress.status}`}>
                    {progress.status === "completed" && (
                      <CheckCircleIcon className="parallel-modal-status-icon" />
                    )}
                    {progress.status === "failed" && (
                      <DoDisturbIcon className="parallel-modal-status-icon" />
                    )}
                    {(progress.status === "processing" || progress.status === "pending") && (
                      <CircularProgress
                        size={12}
                        style={{ color: "var(--application-primary-color)" }}
                      />
                    )}
                  </div>

                  <div className="parallel-modal-file-info">
                    <div className="parallel-modal-file-name" style={{ fontFamily }}>
                      {progress.fileName}
                    </div>
                    <div className="parallel-modal-file-message" style={{ fontFamily }}>
                      {progress.message}
                    </div>
                  </div>

                  <div className="parallel-modal-file-meta">
                    {docLabel && (
                      <span
                        className={`parallel-modal-doc-label ${progress.uploadType != null ? "identified" : "unidentified"}`}
                        style={{ fontFamily }}
                      >
                        {docLabel}
                      </span>
                    )}
                    {overallConf && (
                      <span className="parallel-modal-confidence-badge" style={{ fontFamily }}>
                        {overallConf}
                      </span>
                    )}
                    <div
                      className={`parallel-modal-status-badge ${progress.status}`}
                      style={{ fontFamily }}
                    >
                      {progress.status.toUpperCase()}
                    </div>
                  </div>
                </div>

                {/* ── Per-form detail rows ── */}
                {isDone && docs.length > 0 && (
                  <div className="parallel-modal-docs-table" style={{ fontFamily }}>
                    <div className="parallel-modal-docs-header">
                      <span>Form Type</span>
                      <span>Confidence</span>
                      <span>Pages</span>
                    </div>
                    {docs.map((doc, i) => {
                      const confPct =
                        doc.confidence != null
                          ? Math.round(doc.confidence <= 1 ? doc.confidence * 100 : doc.confidence)
                          : null;
                      const confClass =
                        confPct == null
                          ? ""
                          : confPct >= 80
                            ? "conf-high"
                            : confPct >= 50
                              ? "conf-mid"
                              : "conf-low";
                      const pageNums = doc.pages?.length > 0 ? doc.pages.join(", ") : "—";
                      const pageCount = doc.pages?.length ?? 0;
                      return (
                        <div key={i} className="parallel-modal-docs-row">
                          <span className="parallel-modal-doc-type-cell">
                            {doc.doc_type || "Unknown"}
                          </span>
                          <span className={`parallel-modal-conf-cell ${confClass}`}>
                            {confPct != null ? `${confPct}%` : "—"}
                          </span>
                          <span className="parallel-modal-pages-cell" title={`Pages: ${pageNums}`}>
                            {pageCount} {pageCount === 1 ? "page" : "pages"}
                            {pageCount > 0 && (
                              <span className="parallel-modal-page-nums"> (p.{pageNums})</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="parallel-modal-footer">
          {allDone ? (
            <button className="parallel-modal-done-btn" style={{ fontFamily }} onClick={handleDone}>
              Close
            </button>
          ) : (
            <>
              <div className="parallel-modal-upload-count" style={{ fontFamily }}>
                {isSingleUpload
                  ? "Uploading 1 file..."
                  : `Uploading ${uploadCount} ${uploadingMessage}`}
              </div>
              {!isSingleUpload && (
                <div className="parallel-modal-processing-info" style={{ fontFamily }}>
                  {processingMessage}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};
